'use client';

import Link from 'next/link';
import { useParams, usePathname } from 'next/navigation';

import Text from '@/components/ui/Text';
import { COMMON_I18N } from '@/lib/i18n/commonI18n';
import { cn } from '@/utils/cn';

const LanguageSwitcher = ({
  className,
  onChangeLocale,
}: LanguageSwitcherProps) => {
  const { locale: raw } = useParams<{ locale?: string }>();
  const pathname = usePathname();

  const current: Locale = raw === 'en' ? 'en' : 'pt';
  const locales: Locale[] = ['pt', 'en'];

  return (
    <nav
      aria-label={COMMON_I18N[current].languageSwitcher}
      className={cn('inline-flex items-center px-[10px] py-[10px]', className)}
    >
      {locales.map((locale) => {
        const isActive = current === locale;

        return (
          <div
            key={locale}
            className="
              relative flex items-center
              not-first:pl-4
              not-first:before:absolute
              not-first:before:left-[6px]
              not-first:before:top-1/2
              not-first:before:-translate-y-1/2
              not-first:before:h-4
              not-first:before:w-[2px]
              not-first:before:bg-primary

            "
          >
            <Link
              href={hrefForLocale(pathname, locale)}
              onClick={onChangeLocale}
              className="group relative inline-flex items-center"
              aria-current={isActive ? 'true' : undefined}
            >
              <Text as="span" variant="lSButton">
                {locale.toUpperCase()}
              </Text>

              <span
                aria-hidden
                className={cn(
                  'pointer-events-none absolute left-1/2 -bottom-[6px] h-[3px] w-full -translate-x-1/2 origin-center scale-x-0 bg-primary transition-transform duration-300 ease-out',
                  'group-hover:scale-x-100',
                  isActive && 'scale-x-100',
                )}
              />
            </Link>
          </div>
        );
      })}
    </nav>
  );
};

export default LanguageSwitcher;

type Locale = 'en' | 'pt';

type LanguageSwitcherProps = {
  className?: string;
  onChangeLocale?: () => void;
};

function hrefForLocale(pathname: string, locale: Locale) {
  const segments = pathname.split('/');
  const hasLocalePrefix = segments[1] === 'en' || segments[1] === 'pt';

  if (hasLocalePrefix) {
    segments[1] = locale;
    return segments.join('/');
  }

  return `/${locale}${pathname}`;
}
